import { useState, useContext } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import Link from '@mui/material/Link';
import LaunchIcon from '@mui/icons-material/Launch';

import DataGrid from 'app/components/DataGrid';
import classes from 'app/components/DataGrid/classes.module.scss';
import MomentsUploadModal from 'app/components/Uploads/MomentsUploadModal';
import useMoments from 'app/hooks/useMoments';
import MomentUploadProgressBar from './MomentUploadProgressBar';
import UploadWorker from 'app/workers/UploadWorker';
import MomentModal from './MomentModal';
import MomentCell from 'app/components/DataGrid/MomentCell';
import useMomentsUploadForm from 'app/hooks/useMomentsUploadForm';
import useDeeplink from 'app/hooks/useDeeplink';
import CustomersCell from 'app/components/DataGrid/CustomersCell';
import JSZip from 'jszip';

import { MOMENT_DOWNLOAD_URL } from 'shared/utils/Constants';
import {
  downloadMomentsToZip,
  downloadMomentBlob
} from 'shared/services/DownloadService';

import {
  getMoments,
  setMomentParams
} from 'app/actions/moments';



import {
  createMoment,
  updateMoment,
  destroyMoment,
  getMoments as exportMomentsSvc
} from 'app/services/moments';

import {
  getJobs,
  getSimpleJobs
} from 'app/services/jobs';

import {
  getCustomers
} from 'app/services/customers';

import {
  formatJobCellValue,
  //formatJobOptions,
  //formatCustomerOptions,
  formatCustomersCellValue,
  formatEllipsis
} from 'app/utils/valueFormatters';

import {
  formatOptions
} from 'app/utils/options';

import {
  setPhotoSwipeOpen,
  setPhotoSwipeMoment
} from 'app/actions/photoSwipeActions';

import {
  customers
} from 'app/resources';

import {
  AND_OPERATOR,
  OR_OPERATOR,
  OPERATOR_KEY
} from 'app/hooks/useFilter';

const Moments = props => {
  // uploading from the Moments grid is only enabled for kiosk accounts
  const account = useSelector(state => state.portalReducer.account);
  const isKioskMode = account.feature_flags.kiosk_mode;
  const showMomentUpload = isKioskMode && !account.feature_flags.disable_upload;

  const dispatch = useDispatch();
  const deeplink = useDeeplink();

  // use useResource for the grid (but not the form)
  const {
    resource,
    onEdit,
    formIsOpen,
    onFormClose,
    onFormSubmit,
    showFilters,
    getRowData,
    updateRow,
    onDataGridLoad,
    currentRecord,
    refreshGrid,
    getFilterModel,
    onDestroy
  } = useMoments();

  // use useMomentsUploadForm for the form and add button
  const onUploadStart = () => {
    refreshGrid();
  }

  const onMomentComplete = moment => {
    // update the moment row so that the progress bar disappears
    updateRow(moment);
  }

  const {
    onAdd,
    formIsOpen: uploadFormIsOpen,
    onFormClose: onUploadFormClose,
    onFormSubmit: onUploadFormSubmit
  } = useMomentsUploadForm({
    onUploadStart,
    onMomentComplete
  });

  // use uploadWorker to generate upload preview while uploading
  const uploadWorker = useContext(UploadWorker);

  const exportCsv = async (row) => {
    let params = {
      page: 0,
      per_page: 1000,
      is_export: true
    }

    const filterModel = getFilterModel();
    if (filterModel) {
      params.filter_model = filterModel;
    }

    exportMomentsSvc(params).then((resp => resp.blob())).then((blob) => {
      const elem = window.document.createElement('a');
      elem.href = window.URL.createObjectURL(blob);
      elem.download = `moments_${new Date().toISOString()}.csv`;
      document.body.appendChild(elem);
      elem.click();
      document.body.removeChild(elem);
    })
  }

  const getJobOptions = async (page = 1, perPage = 100, filter = null) => {
    let jobs = await getSimpleJobs();
    return formatOptions(jobs);
  }

  const header = 'Moments';
  const addButtonLabel = 'Upload Moments';
  const columnOrder = ['file', 'status', 'filename', 'size', 'uploaded', 'job_id', 'customer_id', 'date'];
  const visibleColumns = columnOrder;
  const omitColumns = ['job', 'customers'];

  const onMomentClick = moment => {
    dispatch(setPhotoSwipeMoment(moment));
    dispatch(setPhotoSwipeOpen(true));
  }

  const setPhotoSwipeItem = moment => {
    return {
      id: moment.id,
      src: moment.preview_url,
      w: moment.width,
      h: moment.height,
      digital_edit: moment.digital_edit,
      orig_moment_id: moment.orig_moment_id,
    }
  };

  const viewCustomersForMoment = id => {
    let filterModel = {
      moment_id: [{
        filterClass: 'text',
        filterType: 'equals',
        value: id.toString(),
        operator: AND_OPERATOR
      }]
    }

    return deeplink.load(customers, filterModel);
  }

  const getCustomerLabel = (c) => {
    return c.name || c.email || c.access_code || c.id.toString()
  }

  const getCustomerOptions = async (page, perPage, filterValue) => {
    let filterModel = {
      [OPERATOR_KEY]: [{
        value: OR_OPERATOR
      }],
      name: [{
        filterClass: 'text',
        filterType: 'contains',
        value: filterValue,
        operator: OR_OPERATOR
      }],
      email: [{
        filterClass: 'text',
        filterType: 'contains',
        value: filterValue,
        operator: OR_OPERATOR
      }],
      phone: [{
        filterClass: 'text',
        filterType: 'contains',
        value: filterValue,
        operator: OR_OPERATOR
      }],
      access_code: [{
        filterClass: 'text',
        filterType: 'contains',
        value: filterValue,
        operator: OR_OPERATOR
      }]
    }

    let params = {
      page,
      per_page: perPage,
      filter_model: filterModel,
      sort_model: null
    }

    let resp = await getCustomers(params);

    return formatOptions(resp.records, { label: getCustomerLabel });
  }

  const columnDefOverrides = [
    {
      field: 'file',
      headerName: 'File',
      pinned: 'left',
      inputType: 'number',
      width: 10 * 16,
      thumbnail: async data => {
        if (data) {
          if (data.uploaded) {
            return data.thumb_url;
          } else {
            return new Promise(resolve => {
              // moment files have moment.id as the fileId
              let fileId = data.id;
              uploadWorker.loadPreview(fileId, resolve);
            });
          }
        }
        return null;
      },
      onThumbnailClick: data => onMomentClick(data),
      cellRenderer: params => {
        const {
          data,
          colDef
        } = params;

        if (data) {
          return (
            <MomentCell
              data={data}
              colDef={colDef}
              showUpload={false}
              moment={data}
              onUploadComplete={onMomentComplete}
            />
          );
        }
        return '';
      }
    },
    {
      field: 'filename',
      valueFormatter: value => formatEllipsis(value, 32)
    },
    {
      field: 'job_id',
      headerName: isKioskMode ? 'Catalog' : 'Job',
      minWidth: 250,
      renderValues: data => data.job && [data.job.name, data.job.event_id],
      dataValueFormatter: (data, value) => {
        // if kiosk mode then filter out the main job (non-catalog jobs)
        if (isKioskMode && !data.job?.kiosk_id) {
          return null;
        }

        return formatJobCellValue(data.job);
      },
      quickEdit: true,
      inputType: 'autocomplete',
      getInputOptions: getJobOptions
    },
    {
      field: 'customer_id',
      headerName: 'Customers',
      cellRenderer: params => {
        let data = params.data;
        if (data?.customers?.length) {
          const {
            id,
            customers,
            matched_customers
          } = data;

          const onClick = () => viewCustomersForMoment(id);

          return (
            <CustomersCell
              customers={customers}
              customerCount={customers.length}
              onClick={onClick}
              matchedCustomers={matched_customers}
            />
          );
        }
        return '';
      },
      inputType: 'autocomplete',
      getInputOptions: getCustomerOptions
    },
    { field: 'date', formatType: 'date', inputType: 'date' },
    { field: 'uploaded', formatType: 'date', inputType: 'date' },
    { field: 'preview', headerName: 'Thumbnailed', formatType: 'date', inputType: 'date' },
    { field: 'created_at', formatType: 'date', inputType: 'date' },
    { field: 'updated_at', formatType: 'date', inputType: 'date' },
    {
      field: 'status',
      width: 220,
      cellRenderer: params => {
        let data = params.data;
        if (data) {
          if (data.uploaded) {
            return <span className={classes.gridCell}>Uploaded</span>;
          } else {
            return <MomentUploadProgressBar moment={data} />;
          }
        }
        return '';
      },
      filterDisabled: true
      //inputType: 'select',
      //inputOptions: statusOptions
    },
    { field: 'size', inputType: 'number', width: 150, formatType: 'bytes' }
  ]

  const onDelete = async selectedRows => {
    console.log(selectedRows);
    let ids = selectedRows.map(row => row.id);
    await onDestroy(ids);
  }

  const onDownload = async (selectedRows) => {
    if (selectedRows.length > 0) {
      if (selectedRows.length === 1) {
        downloadMomentBlob(selectedRows[0], false)
      } else {
        await downloadMomentsToZip(selectedRows);
      }
    }
  }

  const actions = [
    { key: 'destroy', label: 'Delete Moments', callback: onDelete },
    { key: 'download', label: 'Download Moments', callback: onDownload },

    // TODO: Hiding Moments export for now as it doesn't seem to provide any usual info for clients
    // { key: 'export', singleAction: true, label: 'Export to CSV', callback: exportCsv }
  ]

  return (
    <div>
      <DataGrid
        resource={resource}
        header={header}
        getRowData={getRowData}
        addButtonLabel={addButtonLabel}
        //onAdd={showMomentUpload && onAdd}
        showEdit={true}
        onEdit={onEdit}
        showQuickEdit={true}
        columnOrder={columnOrder}
        visibleColumns={visibleColumns}
        columnDefOverrides={columnDefOverrides}
        omitColumns={omitColumns}
        setPhotoSwipeItem={setPhotoSwipeItem}
        showFilters={showFilters}
        loadingMessage="Loading moments..."
        showSelect={true}
        actions={actions}
        onDataGridLoad={onDataGridLoad}
      />
      <MomentsUploadModal
        isOpen={uploadFormIsOpen}
        onClose={onUploadFormClose}
        onFormSubmit={onUploadFormSubmit}
      />
      <MomentModal
        moment={currentRecord}
        isOpen={formIsOpen}
        onClose={onFormClose}
        onFormSubmit={onFormSubmit}
        onUploadComplete={onMomentComplete}
        onDestroy={onDestroy}
      />
    </div>
  );
}

export default Moments;
